import { useCallback, useState } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import { $HashtagPopularPost, $HashtagRecentPost, $HashtagRecentPostLoadmore } from 'shared/atoms';
import { Article } from 'shared/models/legacy';
import { ArticleCard } from '~/components/article-card';
import { EmptyPosts } from '~/components/empty-posts';
import { SeeMore } from '~/components/see-more';
import { DivisibleStack } from '~/components/divisible-stack';
import { useTranslation } from 'react-i18next';
import { ModuleType } from '~/utils/module-type';
import SectionTitle from '~/components/section-title/index';
import { $CurrentPageModule } from '~/models/current-page-module';
import { ScopedProvider, useRouteLoader } from '@lemon8/web-app-shared/ssr';
import { useViewTracking } from '~/utils/use-view-tracking';
import { $CurrentHashtagId } from '~/routes/topic.$hashtag_id/shared/atoms';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';
import { ClickPositionPrefix } from '~/utils/click-position';

import './style.scss';

export default function HashtagPostSection(): JSX.Element | null {
  const { t } = useTranslation();
  const hashtagId = useAtomValue($CurrentHashtagId);
  const { items: popularPostItems = [] } = useAtomValue($HashtagPopularPost(hashtagId));
  const [recentPostData] = useAtom($HashtagRecentPost(hashtagId));

  const { items: recentPosts = [], hasMore = false, sessionImprId = '' } = recentPostData;

  const loader = useRouteLoader(
    'hashtag-post-load-more',
    {
      session_impr_id: sessionImprId,
    },
    {
      atomFamilies: {
        $HashtagRecentPostLoadmore,
      },
    },
  );

  const handleSeeMore = (): void => {
    if (loader.state === 'loading') {
      return;
    }
    loader.load();
  };

  const popularObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.related_article,
      });
    }, []),
  });

  const recentObservedRef = useViewTracking({
    onView: useCallback(() => {
      window.Tea('wap_module_show', {
        current_page_module: ModuleType.related_article,
      });
    }, []),
  });

  return (
    <>
      <section id="hashtag-popular-post-section" ref={popularObservedRef}>
        {popularPostItems.length >= 10 && (
          <ScopedProvider
            atomsWithHydration={[
              [$CurrentPageModule, ModuleType.related_article],
              [$CurrentPositionPrefix, ClickPositionPrefix.none],
            ]}
          >
            <SectionTitle title={t('wap_hashtag_popular_title')} />
            <DivisibleStack wrapper="article-list" divider="article-list-divider">
              {popularPostItems.map((a: Article, rank: number) => (
                <ArticleCard article={a} key={a.groupId} rank={rank} />
              ))}
            </DivisibleStack>
          </ScopedProvider>
        )}
      </section>
      <section id="hashtag-recent-post-section" ref={recentObservedRef}>
        {recentPosts.length > 0 ? (
          <ScopedProvider
            atomsWithHydration={[
              [$CurrentPageModule, ModuleType.related_article],
              [$CurrentPositionPrefix, ClickPositionPrefix.none],
            ]}
          >
            <div id="hashtag-post-section-divider" />
            <SectionTitle title={t('wap_hashtag_recent_title')} />
            <DivisibleStack wrapper="article-list" divider="article-list-divider">
              {recentPosts.map((a: Article, rank: number) => (
                <ArticleCard article={a} key={a.groupId} rank={rank} />
              ))}
            </DivisibleStack>
            {hasMore && <SeeMore onClickSeeMore={handleSeeMore} loading={loader.state === 'loading'} />}
          </ScopedProvider>
        ) : (
          <section className="hashtag-post-section">
            <SectionTitle title={t('wap_hashtag_recent_title')} />
            <EmptyPosts />
          </section>
        )}
      </section>
    </>
  );
}
